import React from 'react';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import { StudioCompactSelect } from 'src/components/star-grid/components/StudioCompactSelect';
import { StudioDeckSettings } from 'src/components/star-grid/components/StudioDeckSettings';
import { StudioTemplatePicker } from 'src/components/star-grid/components/StudioTemplatePicker';
import type { CatalogScreen, CatalogTemplate, ReportSummary } from 'src/components/star-grid/api/reportEngineTypes';
import { StudioDetailsFields } from 'src/components/star-grid/components/StudioDetailsFields';
import styles from 'src/components/star-grid/components/styles/StudioHeader.module.scss';
export interface StudioStartingPointProps {
  /** Seeds the spec from a template. */
  readonly onStartTemplate?: (template: CatalogTemplate) => void;
  /** Loads a deck screen's own arrangement into the spec. */
  readonly onImportScreen?: (screen: CatalogScreen) => void;
  /** Loads a saved report's arrangement into the spec. */
  readonly onImportReport?: (report: ReportSummary) => void;
  /** What the Template control shows — the template label, or the imported report's name. */
  readonly templateLabel?: string;
  /** The data source picker, shown in the details row beside Template. Omitted leaves the P&L layout unchanged. */
  readonly dataSourceControl?: React.ReactNode;
}
interface Props extends StudioStartingPointProps {
  readonly state: ReportStudioState;
  readonly chooser?: React.ReactNode;
}
export const StudioReportDetails: React.FC<Props> = ({
  state,
  chooser,
  onStartTemplate,
  onImportScreen,
  onImportReport,
  templateLabel,
  dataSourceControl
}) => (
  <div className={styles.details}>
    <div className={styles.detailsTitle}>
      <h2>{state.text('createReportDetails')}</h2>
    </div>
    <div className={styles.detailsGrid}>
      {/* Create only: when EDITING, the report picker names the report, so a second name field reads as
          a rename box -- and an empty column here pushed the pickers to the right. The draft keeps the
          saved name and description either way. */}
      {!state.editingReportId && <StudioDetailsFields state={state} />}
      <div className={styles.templateColumn}>
        <div className={styles.templateFields}>
          {dataSourceControl}
          {/* Editing, the chooser is the report picker; creating, the template picker below replaces it. */}
          {Boolean(state.editingReportId) && chooser}
          <StudioDeckSettings state={state} />
          {/* The one place a report's starting point is chosen: a template, a deck screen or a saved report. */}
          {onStartTemplate && onImportScreen && onImportReport && !state.editingReportId && (
            <div className={styles.picker} data-testid="studio-template-picker">
              <StudioTemplatePicker
                text={state.text}
                selectedLabel={templateLabel}
                onSelectTemplate={onStartTemplate}
                onImportScreen={onImportScreen}
                onImportReport={onImportReport}
              />
            </div>
          )}
          {state.draft.definition.deck_types.length > 0 && (
            <label className={`${styles.field} ${styles.picker}`}>
              <span className={styles.fieldLabel}>{state.text('studioDeckLayout')}</span>
              <StudioCompactSelect
                label={state.text('studioDeckLayout')}
                value={state.draft.definition.active_deck}
                options={state.draft.definition.deck_types.map((deck) => ({ id: deck, label: deck }))}
                onChange={(active_deck) => state.definition({ active_deck })}
                testId="studio-active-deck"
              />
            </label>
          )}
        </div>
      </div>
    </div>
  </div>
);
